import { Box, FormLabel, List, ListItem, TextField } from "@mui/material";
import { ChangeEvent, HTMLInputTypeAttribute, useEffect } from "react";
import { useTranslation } from "react-i18next";

import AutoCompleteSelect from "../../../../../components/autocomplete/autoCompleteSelect/AutoCompleteSelect";
import { InboundDeliveryDocumentType } from "../../../../../models/autogenerated-deliveryService-gql/graphql";
import { IDocument } from "../../../../../models/document";
import { isNullOrEmpty } from "../../../../../utils/helper";

interface AddDocumentItemFormProps {
  formData: IDocument;
  onChange: (fieldID: string, value: string | File) => void;
  setIsFormValid: (isValid: boolean) => void;
}

const requiredFields: (keyof IDocument)[] = ["name", "documentType", "file"];

const AddDocumentItemForm = (props: AddDocumentItemFormProps) => {
  const { t } = useTranslation();
  const { formData, onChange, setIsFormValid } = props;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target;

    //we need to handle a file upload differently from a text input
    if (id === "file") {
      const file = event.target.files?.[0];
      if (file) onChange(id, file);

      return;
    }

    onChange(id, value);
  };

  useEffect(() => {
    const isFormValid = requiredFields.every(
      (field) => !isNullOrEmpty(formData[field]),
    );

    setIsFormValid(isFormValid);
  }, [formData]);

  const renderTextField = (
    label: keyof IDocument,
    required?: boolean,
    type?: HTMLInputTypeAttribute,
    error?: string,
  ) => (
    <div style={{ width: "100%" }}>
      <FormLabel required={required}>{t(label)}</FormLabel>
      <TextField
        type={type}
        data-testid={`document-item-form-textfield-${label}`}
        id={label}
        className="input-element"
        value={label === "file" ? undefined : formData[label]} //we need the TextFields value to be uncontrolled when it's a file input. It's just how it needs to be
        onChange={handleChange}
        variant="outlined"
        required={required}
        error={!!error}
        helperText={error}
      />
    </div>
  );

  const renderAutoCompleteSelect = (
    label: keyof IDocument,
    options: string[],
    required?: boolean,
  ) => (
    <AutoCompleteSelect
      fieldId={label}
      options={options}
      onSelect={onChange}
      initialValue={formData[label]}
      required={required}
    />
  );

  return (
    <Box className="item-form-container" component="form" autoComplete="off">
      <List title={t("requiredInformation")}>
        <ListItem>{renderTextField("name", true, "text")}</ListItem>
        <ListItem>
          {renderAutoCompleteSelect(
            "documentType",
            Object.values(InboundDeliveryDocumentType),
            true,
          )}
        </ListItem>
        <ListItem>{renderTextField("file", true, "file")}</ListItem>
      </List>
    </Box>
  );
};
export default AddDocumentItemForm;
